import React from 'react'
import { cn } from '@/shared/lib/utils'
import styles from './ActionHeader.module.scss'
import { MaterialIcon } from '@/ui/material-icon'
import { Input } from '@/ui/input'
import { Button } from '@/ui/button'

interface Action {
    label?: string;
    icon?: string;
    onClick: () => void;
    variant?: 'primary' | 'icon';
    ariaLabel?: string;
    key: string;
}

interface ActionHeaderProps {
    title: string
    search?: string
    onSearchChange?: (value: string) => void
    searchPlaceholder?: string
    searchInputClassName?: string
    leadingActions?: Action[]
    actions?: Action[]
    className?: string
    /** Slot rendered between search and action buttons */
    children?: React.ReactNode
}

export default function ActionHeader({
    title,
    search,
    onSearchChange,
    searchPlaceholder = 'Search...',
    searchInputClassName,
    leadingActions = [],
    actions = [],
    className,
    children,
}: Readonly<ActionHeaderProps>) {

    return (
        <div className={cn(styles.actionHeader, className)}>
            <h1 className={styles.pageTitle}>{title}</h1>

            <div className={styles.headerActions}>
                {/* ⚡ Leading actions (e.g. refresh) */}
                {leadingActions.map((action) => {
                    if (action.variant === 'icon') {
                        return (
                            <Button
                                key={action.key}
                                variant="outline"
                                size="icon"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    action.onClick()
                                }}
                                aria-label={action.ariaLabel}
                                className={styles.iconBtn}
                            >
                                {action.icon && <MaterialIcon name={action.icon} size={18} />}
                            </Button>
                        )
                    }

                    return (
                        <Button
                            key={action.key}
                            onClick={(e) => {
                                e.stopPropagation()
                                action.onClick()
                            }}
                            className={styles.primaryBtn}
                        >
                            {action.icon && <MaterialIcon name={action.icon} size={18} />}
                            {action.label}
                        </Button>
                    )
                })}

                {/* 🔍 Search */}
                {search !== undefined && onSearchChange && (
                    <div className={styles.searchWrapper}>
                        <MaterialIcon name="search" className={styles.searchIcon} />
                        <Input
                            value={search}
                            onChange={e => onSearchChange(e.target.value)}
                            placeholder={searchPlaceholder}
                            className={cn(styles.searchInput, searchInputClassName)}
                        />
                    </div>
                )}

                {/* ⚡ Custom slot (e.g. filter dropdown) */}
                {children}

                {/* ⚡ Actions */}
                {actions.map((action) => {
                    // 🔹 Icon Button (e.g. Refresh/Filter)
                    if (action.variant === 'icon') {
                        return (
                            <Button
                                key={action.key}
                                variant="outline"
                                size="icon"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    action.onClick()
                                }}
                                aria-label={action.ariaLabel}
                                className={styles.iconBtn}
                            >
                                {action.icon && <MaterialIcon name={action.icon} size={18} />}
                            </Button>
                        )
                    }

                    // 🔹 Primary Button (e.g. Add User)
                    return (
                        <Button
                            key={action.key}
                            onClick={(e) => {
                                e.stopPropagation()
                                action.onClick()
                            }}
                            className={styles.primaryBtn}
                        >
                            {action.icon && <MaterialIcon name={action.icon} size={18} />}
                            {action.label}
                        </Button>
                    )
                })}
            </div>
        </div>
    )
}